import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

// Illustrative reference integration only.
// Al-Amen Technology does not operate its own model API or SDK; we integrate
// the sovereign, open-source or partner model provider you select. The
// snippets below use an OpenAI-compatible client pattern against a
// provider-supplied, residency-bound endpoint as an example.
const SAMPLES = {
  python: `# Illustrative reference - uses your chosen model provider's SDK
from openai import OpenAI  # any OpenAI-compatible client

client = OpenAI(
    base_url="https://<your-provider-endpoint>/v1",  # sovereign / partner
    api_key="<YOUR_PROVIDER_API_KEY>",
)

stream = client.chat.completions.create(
    model="<provider-model-id>",
    stream=True,
    messages=[
        {"role": "system", "content": "You are a bilingual legal analyst."},
        {"role": "user",   "content": "Summarise UAE Federal Law No. 2 of 2019."},
    ],
)

for event in stream:
    print(event.choices[0].delta.content or "", end="", flush=True)
`,
  typescript: `// Illustrative reference - uses your chosen model provider's SDK
import OpenAI from "openai"; // any OpenAI-compatible client

const client = new OpenAI({
  baseURL: "https://<your-provider-endpoint>/v1", // sovereign / partner
  apiKey: process.env.PROVIDER_API_KEY,
});

const stream = await client.chat.completions.create({
  model: "<provider-model-id>",
  stream: true,
  messages: [
    { role: "system", content: "You are a bilingual legal analyst." },
    { role: "user",   content: "Summarise UAE Federal Law No. 2 of 2019." },
  ],
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content ?? "");
}
`,
  curl: `# Illustrative reference - against your chosen provider endpoint
curl https://<your-provider-endpoint>/v1/chat/completions \\
  -H "Authorization: Bearer $PROVIDER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "<provider-model-id>",
    "stream": true,
    "messages": [
      { "role": "system", "content": "You are a bilingual legal analyst." },
      { "role": "user",   "content": "Summarise UAE Federal Law No. 2 of 2019." }
    ]
  }'
`
};

const LANGS = [
  { id: 'python', label: 'Python' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'curl', label: 'cURL' }
];

export default function CodeSample() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const lang = LANGS[tab];
  const code = useMemo(() => SAMPLES[lang.id], [lang.id]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      /* ignore */
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderColor: 'divider'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          pr: 1
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {
              minHeight: 42,
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
              textTransform: 'none',
              letterSpacing: 0
            }
          }}
        >
          {LANGS.map((l) => (
            <Tab key={l.id} label={l.label} />
          ))}
        </Tabs>
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            color: 'text.secondary',
            mr: 1,
            display: { xs: 'none', sm: 'inline' }
          }}
        >
          {t('home.code.installHint')}
        </Typography>
        <Tooltip title={copied ? t('playgroundDemo.copied') : t('playgroundDemo.copy')}>
          <IconButton onClick={onCopy} size="small">
            {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: { xs: 2, md: 2.5 },
          fontFamily: 'var(--font-mono)',
          fontSize: '0.82rem',
          lineHeight: 1.6,
          color: 'text.primary',
          overflowX: 'auto',
          bgcolor: 'background.paper'
        }}
      >
        <code>{code}</code>
      </Box>
    </Paper>
  );
}
